import { Box, Card, MessageBlock } from '@deckee/deck-hand';
import { DateTime } from 'luxon';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import MuxPlayer from '@mux/mux-player-react';
import { captureEvent } from '../../../../services/event-capture';
import { useAppDispatch } from '../../../../services/store';
import {
  addTemporaryLocationMarker,
  clearTemporaryLocationMarkers,
} from '../../../../services/store/modules/temporaryMarkers';
import SheetLayout, {
  SheetBody,
} from '../../../layouts/SheetLayout/SheetLayout';
import Modal from '../../../shared/Modal/Modal';
import PreviewImage from '../../../shared/PreviewImage/PreviewImage';
import LogoSpinner from '../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import useMoveMapToLocation from '../../Marketplace/hooks/use-move-map-to-location';
import useIncidentReport from '../hooks/useIncidentReport';

const ZoomedImage = styled(Box).attrs(() => ({ as: 'img' }))`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  box-sizing: content-box;
`;

const VideoPlayer = styled(MuxPlayer)`
  width: '100%'
    ${({ theme }) => css`
      bottom: ${theme.space[3]}px;
    `};
`;

const LabelledItem: FC<{ label: string }> = ({ label, children }) => (
  <Box py="1">
    <Box mr="1" fontWeight="bold" as="label" display="inline">
      {label}:
    </Box>

    {children}
  </Box>
);

// A function to format a string into a readable date time with luxon
const formatDateTime = (dateTime?: string) => {
  if (!dateTime) return '';
  return DateTime.fromISO(dateTime).toLocaleString(DateTime.DATETIME_FULL);
};

const ViewIncidentPage = () => {
  const dispatch = useAppDispatch();
  const [selectedImage, setSelectedImage] = useState<string>();
  const { id } = useParams<{ id: string }>();
  const { incident, loading, error } = useIncidentReport(id);

  const ref = useRef<HTMLDivElement>(null);
  const client = ref?.current || { clientHeight: 0, clientWidth: 0 };
  const sheetHeight = client.clientHeight;
  const sheetWidth = client.clientWidth;
  // Hard coded pixel size of when the app switches to mobile size
  const isMobile = (window.innerWidth || 0) <= 800;

  const handleSelectImage = (imageSrc: string) => {
    captureEvent({
      type: 'view-incident-report: select image',
      payload: {},
    });
    setSelectedImage(imageSrc);
  };

  const handleVideoPlayerEvents = (eventName: string, playbackId: string) => {
    captureEvent({
      type: `view-incident-report: video ${eventName}`,
      payload: {
        playbackId,
      },
    });
  };

  const {
    type,
    tags,
    contactEmail,
    contactName,
    contactNumber,
    incidentDateTime,
    latitude,
    longitude,
    vesselRegistration,
    description,
    imageUrls,
    videoUploads,
    createdAt,
  } = incident || {};

  useEffect(() => {
    if (latitude && longitude) {
      dispatch(
        addTemporaryLocationMarker({
          latitude: parseFloat(`${latitude}` || '90'),
          longitude: parseFloat(`${longitude}` || '90'),
        }),
      );
    }

    return () => {
      dispatch(clearTemporaryLocationMarkers());
    };
  }, [dispatch, latitude, longitude]);

  useMoveMapToLocation({
    center: {
      lat: parseFloat(`${latitude || 90}`),
      lon: parseFloat(`${longitude || 90}`),
    },
    offsetY: isMobile ? sheetHeight * 0.45 : 0,
    offsetX: !isMobile ? -sheetWidth * 0.45 : 0,
  });

  const isLoading = (loading || !incident) && !error;

  return (
    <SheetLayout
      maxViewHeight="50"
      minViewHeight="50"
      showMenuBar={false}
      heading="Incident report"
      sheetRef={ref}
    >
      <SheetBody>
        {error && (
          <MessageBlock type="error">Unable to load the incident</MessageBlock>
        )}
      </SheetBody>
      {isLoading && <LogoSpinner />}
      {!isLoading && !error && (
        <SheetBody>
          <Card>
            <Card.Heading>Incident type</Card.Heading>
            <Card.Body>
              {type} {tags && tags.length && `(${tags.join(', ')})`}
            </Card.Body>
          </Card>
          <Card>
            <Card.Heading>Reported by</Card.Heading>
            <Card.Body>
              <LabelledItem label="Contact name">{contactName}</LabelledItem>
              <LabelledItem label="Contact number">
                {contactNumber}
              </LabelledItem>
              <LabelledItem label="Contact email">{contactEmail}</LabelledItem>
              <LabelledItem label="Reported at">
                {formatDateTime(createdAt)}
              </LabelledItem>
            </Card.Body>
          </Card>
          <Card>
            <Card.Heading>Incident details</Card.Heading>
            <Card.Body>
              <LabelledItem label="Date and time of incident">
                {formatDateTime(incidentDateTime)}
              </LabelledItem>
              <LabelledItem label="Latitude">{latitude}</LabelledItem>
              <LabelledItem label="Longitude">{longitude}</LabelledItem>
              <LabelledItem label="Vessel registration">
                {vesselRegistration}
              </LabelledItem>
              <LabelledItem label="Description">{description}</LabelledItem>
            </Card.Body>
          </Card>
          {((imageUrls && imageUrls.length > 0) ||
            (videoUploads && videoUploads.length > 0)) && (
            <Card>
              <Card.Heading>Media</Card.Heading>
              <Card.Body>
                {imageUrls?.map((imageSrc) => (
                  <PreviewImage
                    hideActionButtons
                    key={imageSrc}
                    imageSrc={imageSrc}
                    onClick={() => handleSelectImage(imageSrc)}
                  />
                ))}
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore */}
                <Modal
                  isOpen={!!selectedImage}
                  onClose={() => setSelectedImage(undefined)}
                  addContentPadding
                  addCloseButtonPadding
                >
                  <ZoomedImage src={selectedImage} />
                </Modal>
                {videoUploads?.map(({ playbackId }) => (
                  <Box key={playbackId} w="100%" mb="3">
                    <VideoPlayer
                      streamType="on-demand"
                      playbackId={playbackId}
                      onPlay={() =>
                        handleVideoPlayerEvents('onPlay', playbackId)
                      }
                      onPause={() =>
                        handleVideoPlayerEvents('onPause', playbackId)
                      }
                      onWaiting={() =>
                        handleVideoPlayerEvents('onWaiting', playbackId)
                      }
                      onError={() =>
                        handleVideoPlayerEvents('onError', playbackId)
                      }
                      onEnded={() =>
                        handleVideoPlayerEvents('onEnded', playbackId)
                      }
                    />
                  </Box>
                ))}
              </Card.Body>
            </Card>
          )}
        </SheetBody>
      )}
    </SheetLayout>
  );
};

export default ViewIncidentPage;
